import React, { useState } from 'react';
import {
    ArrowLeft, Edit2, User, Shield, Mail, Phone, Calendar,
    Clock, AlertCircle, Trash2, Power, PowerOff, Unlock, Key,
    CheckCircle, XCircle, Info
} from 'lucide-react';
import UserPermissionsTab from './UserPermissionsTab';

const SystemUserDetailsView = ({
                                   user,
                                   onBack,
                                   onEdit,
                                   onDelete,
                                   onSuspend,
                                   onReactivate,
                                   onResetPassword,
                                   onUserUpdated,
                                   hasPermission
                               }) => {
    const [activeTab, setActiveTab] = useState('basic');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [showSuspendModal, setShowSuspendModal] = useState(false);
    const [suspendReason, setSuspendReason] = useState('');
    const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'SUPERADMIN':
                return 'bg-purple-100 text-purple-800 border-purple-200';
            case 'ADMIN':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'MANAGER':
                return 'bg-indigo-100 text-indigo-800 border-indigo-200';
            case 'DATA_OFFICER':
                return 'bg-teal-100 text-teal-800 border-teal-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'ACTIVE':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'LOCKED':
                return 'bg-amber-100 text-amber-800 border-amber-200';
            case 'SUSPENDED':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'INACTIVE':
                return 'bg-gray-100 text-gray-800 border-gray-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatShortDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleDelete = () => {
        if (deleteConfirmText.toLowerCase() === 'delete') {
            onDelete(user);
            setShowDeleteConfirmation(false);
        }
    };

    const handleSuspend = () => {
        onSuspend(user.id, suspendReason);
        setShowSuspendModal(false);
        setSuspendReason('');
    };

    const handleResetPassword = () => {
        onResetPassword(user.id);
        setShowResetPasswordConfirm(false);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                        title="Back to users list"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>

                    <div className="flex-1">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900">User Details</h2>
                            </div>
                            {hasPermission && hasPermission('USERS:UPDATE') && (
                                <button
                                    onClick={() => onEdit(user)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                                >
                                    <Edit2 className="w-4 h-4" />
                                    Edit User
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* User Header Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6 pb-4 border-b">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-8 h-8 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900">
                                {user.firstName} {user.lastName}
                            </h3>
                            <p className="text-sm text-gray-500">@{user.username || user.email?.split('@')[0]}</p>
                            <p className="text-sm text-gray-500 mt-1">{user.email}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 text-sm rounded-full border ${getRoleBadgeColor(user.roleName || user.role)}`}>
                            <Shield className="w-3 h-3 inline mr-1" />
                            {user.roleLabel || (user.roleName || user.role || '').replace(/_/g, ' ')}
                        </span>
                        <span className={`px-3 py-1 text-sm rounded-full border ${getStatusBadgeColor(user.status)}`}>
                            {user.status}
                        </span>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="border-b mb-6">
                    <nav className="flex -mb-px">
                        <button
                            onClick={() => setActiveTab('basic')}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                                activeTab === 'basic'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Basic Information
                        </button>
                        <button
                            onClick={() => setActiveTab('activity')}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                                activeTab === 'activity'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Activity & Access
                        </button>
                        <button
                            onClick={() => setActiveTab('security')}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                                activeTab === 'security'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Security
                        </button>
                        <button
                            onClick={() => setActiveTab('permissions')}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                                activeTab === 'permissions'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Permissions
                        </button>
                    </nav>
                </div>

                {/* Tab Content */}
                {activeTab === 'basic' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Personal Information */}
                        <div className="space-y-4">
                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Personal Information</h4>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-xs text-gray-500">Full Name</p>
                                    <p className="text-sm text-gray-900">{user.firstName} {user.lastName}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Username</p>
                                    <p className="text-sm text-gray-900">{user.username || 'Not set'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Email Address</p>
                                    <p className="text-sm text-gray-900 flex items-center gap-1">
                                        <Mail className="w-3 h-3 text-gray-400" />
                                        {user.email}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Phone Number</p>
                                    <p className="text-sm text-gray-900 flex items-center gap-1">
                                        {user.phoneNumber ? (
                                            <>
                                                <Phone className="w-3 h-3 text-gray-400" />
                                                {user.phoneNumber}
                                            </>
                                        ) : (
                                            <span className="text-gray-400 italic">Not provided</span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Account Information */}
                        <div className="space-y-4">
                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Account Information</h4>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-xs text-gray-500">Role</p>
                                    <p className="text-sm text-gray-900 flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-gray-400" />
                                        {user.roleLabel || (user.roleName || user.role || '').replace(/_/g, ' ')}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Account Status</p>
                                    <p className="text-sm">
                                        {user.status === 'ACTIVE' && (
                                            <span className="flex items-center gap-1 text-green-600">
                                                <CheckCircle className="w-4 h-4" />
                                                Active
                                            </span>
                                        )}
                                        {user.status === 'LOCKED' && (
                                            <span className="flex items-center gap-1 text-amber-600">
                                                <AlertCircle className="w-4 h-4" />
                                                Locked
                                                <span className="text-xs text-gray-500 ml-2">
                                                    (too many failed login attempts)
                                                </span>
                                            </span>
                                        )}
                                        {user.status === 'SUSPENDED' && (
                                            <span className="flex items-center gap-1 text-red-600">
                                                <XCircle className="w-4 h-4" />
                                                Suspended
                                                {user.suspensionReason && (
                                                    <span className="text-xs text-gray-500 ml-2">
                                                        ({user.suspensionReason})
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                        {user.status === 'INACTIVE' && (
                                            <span className="flex items-center gap-1 text-gray-600">
                                                <XCircle className="w-4 h-4" />
                                                Inactive
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Created On</p>
                                    <p className="text-sm text-gray-900 flex items-center gap-1">
                                        <Calendar className="w-3 h-3 text-gray-400" />
                                        {formatShortDate(user.createdAt)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Last Updated</p>
                                    <p className="text-sm text-gray-900 flex items-center gap-1">
                                        <Clock className="w-3 h-3 text-gray-400" />
                                        {formatShortDate(user.updatedAt)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'permissions' && (
                    <UserPermissionsTab
                        key={`${user.id}:${user.updatedAt}`}
                        user={user}
                        canEdit={!!hasPermission && hasPermission('USERS:UPDATE')}
                        onSaved={onUserUpdated}
                    />
                )}

                {activeTab === 'activity' && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Login Activity</h4>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-xs text-gray-500">Last Login</p>
                                        <p className="text-sm text-gray-900">{formatDate(user.lastLogin)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Login Count</p>
                                        <p className="text-sm text-gray-900">{user.loginCount || 0} times</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Failed Login Attempts</p>
                                        <p className="text-sm text-gray-900">{user.failedLoginAttempts || 0}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Access Information</h4>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-xs text-gray-500">Account Locked</p>
                                        <p className="text-sm text-gray-900">
                                            {user.accountLocked ? (
                                                <span className="text-red-600">Yes</span>
                                            ) : (
                                                <span className="text-green-600">No</span>
                                            )}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Password Changed</p>
                                        <p className="text-sm text-gray-900">
                                            {formatShortDate(user.lastPasswordChange)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Must Reset Password</p>
                                        <p className="text-sm text-gray-900">
                                            {user.passwordChangeRequired ? (
                                                <span className="text-orange-600">Yes</span>
                                            ) : (
                                                <span className="text-gray-600">No</span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Activity Logs */}
                        <div className="mt-6 pt-6 border-t">
                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Recent Activity</h4>
                            <div className="bg-gray-50 rounded-lg p-4">
                                <p className="text-sm text-gray-500 italic">Activity logs not available at the moment</p>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'security' && (
                    <div className="space-y-6">
                        {/* Security Actions */}
                        <div className="space-y-4">
                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Security Actions</h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Reset Password */}
                                {hasPermission && hasPermission('USERS:MANAGE') && (
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-start gap-3">
                                            <Key className="w-5 h-5 text-blue-600 mt-1" />
                                            <div className="flex-1">
                                                <h5 className="font-medium text-gray-900">Reset Password</h5>
                                                <p className="text-sm text-gray-600 mt-1 mb-3">
                                                    Send a new password to the user's email address
                                                </p>
                                                <button
                                                    onClick={() => setShowResetPasswordConfirm(true)}
                                                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                                                >
                                                    Reset Password
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Suspend/Reactivate Account */}
                                {hasPermission && hasPermission('USERS:MANAGE') && (
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-start gap-3">
                                            {user.status === 'ACTIVE' ? (
                                                <PowerOff className="w-5 h-5 text-orange-600 mt-1" />
                                            ) : user.status === 'LOCKED' ? (
                                                <Unlock className="w-5 h-5 text-amber-600 mt-1" />
                                            ) : (
                                                <Power className="w-5 h-5 text-green-600 mt-1" />
                                            )}
                                            <div className="flex-1">
                                                <h5 className="font-medium text-gray-900">
                                                    {user.status === 'ACTIVE' ? 'Suspend Account'
                                                        : user.status === 'LOCKED' ? 'Unlock Account'
                                                            : 'Reactivate Account'}
                                                </h5>
                                                <p className="text-sm text-gray-600 mt-1 mb-3">
                                                    {user.status === 'ACTIVE'
                                                        ? 'Temporarily disable user access'
                                                        : user.status === 'LOCKED'
                                                            ? 'Clear the failed-login lockout and restore access'
                                                            : 'Restore user access to the system'}
                                                </p>
                                                {user.status === 'ACTIVE' ? (
                                                    <button
                                                        onClick={() => setShowSuspendModal(true)}
                                                        className="px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                                                    >
                                                        Suspend User
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => onReactivate(user.id)}
                                                        className={`px-3 py-1 text-sm text-white rounded ${
                                                            user.status === 'LOCKED'
                                                                ? 'bg-amber-600 hover:bg-amber-700'
                                                                : 'bg-green-600 hover:bg-green-700'
                                                        }`}
                                                    >
                                                        {user.status === 'LOCKED' ? 'Unlock User' : 'Reactivate User'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Danger Zone */}
                        {hasPermission && hasPermission('USERS:DELETE') && (
                            <div className="border-t pt-6">
                                <h4 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-4">Danger Zone</h4>

                                <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                                    <div className="flex items-start gap-3">
                                        <Trash2 className="w-5 h-5 text-red-600 mt-1" />
                                        <div className="flex-1">
                                            <h5 className="font-medium text-gray-900">Delete User Account</h5>
                                            <p className="text-sm text-gray-600 mt-1 mb-3">
                                                Permanently remove this user and all associated data. This action cannot be undone.
                                            </p>
                                            {!showDeleteConfirmation ? (
                                                <button
                                                    onClick={() => setShowDeleteConfirmation(true)}
                                                    className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded hover:bg-red-100"
                                                >
                                                    Delete User
                                                </button>
                                            ) : (
                                                <div className="space-y-3">
                                                    <p className="text-sm text-red-600 font-medium">
                                                        Type "delete" to confirm:
                                                    </p>
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="text"
                                                            value={deleteConfirmText}
                                                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                                                            placeholder="Type delete to confirm"
                                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                                                        />
                                                        <button
                                                            onClick={handleDelete}
                                                            disabled={deleteConfirmText.toLowerCase() !== 'delete'}
                                                            className={`px-4 py-2 rounded-lg transition ${
                                                                deleteConfirmText.toLowerCase() === 'delete'
                                                                    ? 'bg-red-600 text-white hover:bg-red-700'
                                                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                            }`}
                                                        >
                                                            Confirm Delete
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setShowDeleteConfirmation(false);
                                                                setDeleteConfirmText('');
                                                            }}
                                                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Suspend Modal */}
            {showSuspendModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold mb-4">Suspend User</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Please provide a reason for suspending this user:
                        </p>
                        <textarea
                            value={suspendReason}
                            onChange={(e) => setSuspendReason(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows="3"
                            placeholder="Enter suspension reason..."
                        />
                        <div className="flex gap-2 mt-4">
                            <button
                                onClick={handleSuspend}
                                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                            >
                                Confirm Suspend
                            </button>
                            <button
                                onClick={() => {
                                    setShowSuspendModal(false);
                                    setSuspendReason('');
                                }}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset Password Confirmation */}
            {showResetPasswordConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold mb-4">Reset Password</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Are you sure you want to reset the password for {user.firstName} {user.lastName}?
                            A new password will be sent to their email address.
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={handleResetPassword}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Reset Password
                            </button>
                            <button
                                onClick={() => setShowResetPasswordConfirm(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SystemUserDetailsView;
